import React from 'react';

export interface ToastItem {
  id: string;
  title: string;
  message: string;
  type: 'success' | 'info' | 'warning' | 'error';
  timestamp: number;
}

interface ToastContainerProps {
  toasts: ToastItem[];
  onRemove: (id: string) => void;
}

export const ToastContainer: React.FC<ToastContainerProps> = ({ toasts, onRemove }) => {
  if (!toasts || toasts.length === 0) return null;

  return (
    <div 
      className="fixed top-5 right-5 z-[9999] flex flex-col gap-3 max-w-sm sm:max-w-md w-full px-4 pointer-events-none"
      role="region"
      aria-label="System Notifications"
      aria-live="polite"
      aria-atomic="true"
    >
      {toasts.map((toast) => {
        const icons = {
          success: 'fa-solid fa-circle-check text-emerald-500',
          info: 'fa-solid fa-bell text-indigo-500',
          warning: 'fa-solid fa-triangle-exclamation text-amber-500',
          error: 'fa-solid fa-circle-xmark text-rose-500',
        };

        const borderBg = {
          success: 'border-emerald-200 bg-white/95 text-slate-800 shadow-emerald-500/10',
          info: 'border-indigo-200 bg-white/95 text-slate-800 shadow-indigo-500/10',
          warning: 'border-amber-200 bg-white/95 text-slate-800 shadow-amber-500/10',
          error: 'border-rose-200 bg-white/95 text-slate-800 shadow-rose-500/10',
        };

        return (
          <div
            key={toast.id}
            role="status"
            aria-label={`${toast.type} notification: ${toast.title}`}
            className={`pointer-events-auto flex items-start gap-3 p-4 rounded-2xl border-2 shadow-2xl backdrop-blur-md transition-all duration-300 animate-in slide-in-from-top-5 fade-in ${borderBg[toast.type]}`}
          >
            <div className="text-xl shrink-0 mt-0.5" aria-hidden="true">
              <i className={icons[toast.type]}></i>
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-xs font-black uppercase tracking-wider text-slate-900 mb-0.5">
                {toast.title}
              </h4>
              <p className="text-xs font-medium text-slate-600 leading-relaxed break-words">
                {toast.message}
              </p>
            </div>
            <button
              onClick={() => onRemove(toast.id)}
              className="text-slate-400 hover:text-slate-700 transition-colors p-1 shrink-0 rounded-lg hover:bg-slate-100"
              aria-label={`Dismiss ${toast.title} notification`}
            >
              <i className="fa-solid fa-xmark text-sm" aria-hidden="true"></i>
            </button>
          </div>
        );
      })}
    </div>
  );
};
